import React from 'react';
import CKEditor from '@ckeditor/ckeditor5-react';
import BalloonEditor from 'ckeditor-ometer';
import './editor.scss';
import {uploadImage} from '../../api/';

class MyUploadAdapter {
    constructor( loader ) {
        // The file loader instance to use during the upload.
        this.loader = loader;
    }

    // Starts the upload process.
    upload() {
        return this.loader.file
            .then( file => new Promise( async ( resolve, reject ) => {
								const downloadURL = await uploadImage(file);
								resolve({default: downloadURL});
            } ) );
    }

    // Aborts the upload process.
    abort() {
        if ( this.xhr ) {
            this.xhr.abort();
        }
    }
}

// ...

function MyCustomUploadAdapterPlugin( editor ) {
    editor.plugins.get( 'FileRepository' ).createUploadAdapter = ( loader ) => {
        // Configure the URL to the upload script in your back-end here!
        return new MyUploadAdapter( loader );
    };
}

const Editor = ({context, onChange, onInit, data, disabled }) => {
	return (
		<div className="content-editor" data-context={context || null}>
			<CKEditor
				config={{
					heading: {
			            options: [
							{ model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
							{ model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
							{ model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
							{ model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
							{ model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
							{
								model: 'headingFancy',
								view: {
								    name: 'cite',
								    classes: 'quote-label'
								},
								title: 'Quote Label - For Attribution in Block Quote',
								class: 'ck-quote_label',

								// It needs to be converted before the standard 'heading2'.
								converterPriority: 'high'
							}
	            		]
	        		},
					mediaEmbed: {
						providers: [
							{
								name: 'spotify',
								url: [
									/^open\.spotify\.com\/(artist\/\w+)/,
									/^open\.spotify\.com\/(album\/\w+)/,
									/^open\.spotify\.com\/(playlist\/\w+)/,
									/^open\.spotify\.com\/(track\/\w+)/
								],
								html: match => {
									const id = match[ 1 ];
									return `<iframe src="https://open.spotify.com/embed/${ id }" width="100%" height="330" frameborder="0" allowtransparency="true" allow="encrypted-media"></iframe>`
								}
							},
							{
								name: 'youtube',
								url: [
									/^(?:m\.)?youtube\.com\/watch\?v=([\w-]+)/,
									/^(?:m\.)?youtube\.com\/v\/([\w-]+)/,
									/^youtube\.com\/embed\/([\w-]+)/,
									/^youtu\.be\/([\w-]+)/
								],
								html: match => {
									const id = match[ 1 ];

									return (
										`<div class="youtube-embed" data-source-id="${ id }" style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 56.2493%;">
											<iframe src="https://www.youtube.com/embed/${ id }" srcdoc="<style>*{padding:0;margin:0;overflow:hidden}html,body{height:100%}img,span{position:absolute;width:100%;top:0;bottom:0;margin:auto}span{height:1.5em;text-align:center;font:48px/1.5 sans-serif;color:white;text-shadow:0 0 0.5em black}</style><a href='https://www.youtube.com/embed/${ id }?autoplay=1'><img src='https://img.youtube.com/vi/${ id }/sddefault.jpg' alt='Play Youtube Video' loading=lazy><span>▶</span></a><!--endSrcDoc-->"
												style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;"
												frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen>
											</iframe>
										</div>`
									);
								}
							},
							{
								name: 'vimeo',
								url: [
									/^vimeo\.com\/(\d+)/,
									/^vimeo\.com\/[^/]+\/[^/]+\/video\/(\d+)/,
									/^vimeo\.com\/album\/[^/]+\/video\/(\d+)/,
									/^vimeo\.com\/channels\/[^/]+\/(\d+)/,
									/^vimeo\.com\/groups\/[^/]+\/videos\/(\d+)/,
									/^vimeo\.com\/ondemand\/[^/]+\/(\d+)/,
									/^player\.vimeo\.com\/video\/(\d+)/
								],
								html: match => {
									const id = match[ 1 ];

									return (
										'<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 56.2493%;">' +
											`<iframe src="https://player.vimeo.com/video/${ id }" ` +
												'style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ' +
												'frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen>' +
											'</iframe>' +
										'</div>'
									);
								}
							},
							{
								name: 'instagram',
								url: /^instagram\.com\/p\/(\w+)/
							},
							{
								name: 'twitter',
								url: /^twitter\.com/
							},
							{
								name: 'googleMaps',
								url: /^google\.com\/maps/
							},
							{
								name: 'flickr',
								url: /^flickr\.com/
							},
							{
								name: 'facebook',
								url: /^facebook\.com/
							}
						],
						extraPlugins: [ MyCustomUploadAdapterPlugin ],
						extraProviders: [
							{
								name: 'bandcamp',
								url: /src="https:\/\/bandcamp.com\/EmbeddedPlayer\/album=(\d*)\/(?:.*track=(\d*))?.*href="(.*)"/,
								html: match => {
									const album = match[1];
									const track = match[2];
									const url = match[3];
									return `<iframe style="border: 0; width: 100%; max-width: 700px; height: 120px;" src="https://bandcamp.com/EmbeddedPlayer/album=${album}/size=large/bgcol=ffffff/linkcol=0687f5${track ? '/track=' + track : ''}/tracklist=false/artwork=small/transparent=true/" seamless><a href="${url}">Hear more at Bandcamp.com</a></iframe>`
								}
							}
						],
						previewsInData: true
					}
				}}
				editor={ BalloonEditor }
				data={data}
				onInit={editor => {
					editor.plugins.get("FileRepository").createUploadAdapter = function(loader) {
						onInit();
						return new MyUploadAdapter(loader);
					};
				} }
				onChange={ ( event, editor ) => {
					const data = editor.getData();
					// console.log( { event, editor, data } );
					onChange(data);
				} }
				onBlur={ ( event, editor ) => {
					//console.log( 'Blur.', editor );
				} }
				onFocus={ ( event, editor ) => {
					//console.log( 'Focus.', editor );
				}}
				disabled={disabled}
			/>
		</div>
	);
};

export default Editor;
